import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CreditCard, Pencil, RefreshCw } from "lucide-react";
import { StatusPill } from "@/components/brand/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { AdminEventDetailDTO } from "@/pages/admin/events/types";
import {
  InvoiceSlot,
  InvoiceSync,
  StripeInvoiceStatus,
  StripeSyncResult,
  stripeInvoiceService,
} from "@/services/stripeInvoiceService";

const STATUS_LABELS: Record<StripeInvoiceStatus, string> = {
  draft: "Draft",
  open: "Open (unpaid)",
  paid: "Paid",
  void: "Void",
  uncollectible: "Uncollectible",
  missing: "Not found in Stripe",
};

interface StripeInvoicesSectionProps {
  event: AdminEventDetailDTO;
  /** Called after anything here changed the event, so the page can reload it. */
  onInvoicesChanged: () => void;
}

/**
 * The event's Stripe invoices, with the two ways an admin fixes them when they
 * have drifted from Stripe: correcting the ids, and syncing with Stripe.
 */
export default function StripeInvoicesSection({
  event,
  onInvoicesChanged,
}: StripeInvoicesSectionProps) {
  const { toast } = useToast();
  const [lastSync, setLastSync] = useState<StripeSyncResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [firstId, setFirstId] = useState("");
  const [secondId, setSecondId] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const showSecond = event.splitPayment || !!event.stripeSecondInvoiceId;
  const hasInvoices = !!event.stripeInvoiceId || !!event.stripeSecondInvoiceId;

  const reportSync = (result: StripeSyncResult, title: string) => {
    setLastSync(result);
    const replaced = result.invoices.filter((i) => i.replacedInvoiceId);
    const notes = [
      result.signupsOpened
        ? "A payment was found, so signups are now open."
        : null,
      ...replaced.map(
        (i) =>
          `Replaced voided invoice ${i.replacedInvoiceId} with its revision ${i.invoiceId}.`,
      ),
    ].filter(Boolean);
    toast({
      title,
      description: notes.length
        ? notes.join(" ")
        : "The invoices match what Stripe has.",
      variant: "success",
    });
    onInvoicesChanged();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      reportSync(
        await stripeInvoiceService.sync(event.eventId),
        "Synced with Stripe",
      );
    } catch (error) {
      toast({
        title: "Sync failed",
        description: errorMessage(error, "Could not reach Stripe."),
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const openEdit = () => {
    setFirstId(event.stripeInvoiceId ?? "");
    setSecondId(event.stripeSecondInvoiceId ?? "");
    setEditError(null);
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setEditError(null);
    try {
      const result = await stripeInvoiceService.updateIds(event.eventId, {
        stripeInvoiceId: firstId.trim() || null,
        stripeSecondInvoiceId: secondId.trim() || null,
      });
      setEditOpen(false);
      reportSync(result, "Invoice IDs saved");
    } catch (error) {
      setEditError(errorMessage(error, "The invoice IDs could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold flex items-center">
        <CreditCard className="w-5 h-5 mr-2 text-primary" />
        Stripe & Invoicing
      </h3>
      <div className="grid gap-1 pl-7">
        <div className="space-y-0.5">
          <div className="text-base">Split Payment</div>
          <div className="text-sm text-muted-foreground">
            {event.splitPayment
              ? "Split payment is enabled"
              : "Split payment is disabled"}
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <InvoiceDetails
            title="First Invoice"
            invoiceId={event.stripeInvoiceId}
            paidDateTime={event.invoicePaidDateTime}
            sync={findSync(lastSync, "FIRST")}
          />
          {showSecond && (
            <InvoiceDetails
              title="Second Invoice"
              invoiceId={event.stripeSecondInvoiceId}
              paidDateTime={event.secondInvoicePaidDateTime}
              sync={findSync(lastSync, "SECOND")}
            />
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing || !hasInvoices}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Syncing..." : "Sync with Stripe"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={openEdit}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit invoice IDs
          </Button>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit invoice IDs</DialogTitle>
            <DialogDescription>
              Saving checks each ID exists in Stripe and belongs to this client,
              then syncs. Leave an ID blank to clear it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stripe-first-invoice-id">First invoice ID</Label>
              <Input
                id="stripe-first-invoice-id"
                value={firstId}
                onChange={(e) => setFirstId(e.target.value)}
                placeholder="in_..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stripe-second-invoice-id">
                Second invoice ID
              </Label>
              <Input
                id="stripe-second-invoice-id"
                value={secondId}
                onChange={(e) => setSecondId(e.target.value)}
                placeholder="in_..."
              />
            </div>
            {editError && (
              <p role="alert" className="text-sm text-destructive">
                {editError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceDetails({
  title,
  invoiceId,
  paidDateTime,
  sync,
}: {
  title: string;
  invoiceId: string | null;
  paidDateTime: string | null;
  sync: InvoiceSync | undefined;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium">{title}</h4>
        {!invoiceId ? (
          <StatusPill tone="muted">No invoice</StatusPill>
        ) : paidDateTime ? (
          <StatusPill tone="success">Paid</StatusPill>
        ) : (
          <StatusPill tone="destructive">Unpaid</StatusPill>
        )}
      </div>
      <div className="grid gap-2">
        <div className="text-sm">
          <span className="font-medium">Invoice ID:</span>{" "}
          <span className="text-muted-foreground">
            {invoiceId || "Not available"}
          </span>
        </div>
        <div className="text-sm">
          <span className="font-medium">Paid Date:</span>{" "}
          <span className="text-muted-foreground">
            {paidDateTime
              ? format(new Date(paidDateTime), "PPP p")
              : "Not paid"}
          </span>
        </div>
        {sync && (
          <div className="text-sm">
            <span className="font-medium">In Stripe:</span>{" "}
            <span className="text-muted-foreground">
              {STATUS_LABELS[sync.status] ?? sync.status}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function findSync(
  result: StripeSyncResult | null,
  slot: InvoiceSlot,
): InvoiceSync | undefined {
  return result?.invoices.find((i) => i.slot === slot);
}

/** The backend's own reason when it gave one, such as a refused invoice id. */
function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return error.response.data.error;
  }
  return fallback;
}
